import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SYSTEM_PROMPT = `You are Legaldecoder's "Single Clause Decoder". A non-lawyer is pasting ONE legal sentence or short clause. Translate it into warm, plain English and rate its risk.

Rules:
- Be empathetic and friendly, like a knowledgeable friend.
- If the input is not a legal clause (just chit-chat, gibberish, or unrelated text), set "notClause" to true and return a short, kind explanation in "plainEnglish".
- Keep "plainEnglish" under 60 words.
- "riskScore": 0-100. 0-29 low, 30-69 medium, 70-100 high.
- "riskLevel": "low" | "medium" | "high".
- "watchOut": one short sentence (under 25 words) about what to look out for. Empty string if low risk and nothing notable.
- NEVER give legal advice. Informational only.

Return ONLY JSON with this exact shape:
{
  "notClause": boolean,
  "plainEnglish": string,
  "riskScore": number,
  "riskLevel": "low" | "medium" | "high",
  "watchOut": string
}`;

const input = z.object({
  text: z
    .string()
    .trim()
    .min(8, "Paste at least a sentence.")
    .max(1500, "Keep it under 1500 characters."),
});

export type SingleClauseResult =
  | {
      ok: true;
      notClause: boolean;
      plainEnglish: string;
      riskScore: number;
      riskLevel: "low" | "medium" | "high";
      watchOut: string;
    }
  | {
      ok: false;
      reason: "rate_limited" | "ai_credits_exhausted" | "unavailable" | "invalid";
      message?: string;
    };

export const decodeSingleClause = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => input.parse(raw))
  .handler(async ({ data }): Promise<SingleClauseResult> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) return { ok: false, reason: "unavailable" };

    const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: `Decode this clause:\n\n${data.text}` },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!aiRes.ok) {
      if (aiRes.status === 429) return { ok: false, reason: "rate_limited" };
      if (aiRes.status === 402) return { ok: false, reason: "ai_credits_exhausted" };
      return { ok: false, reason: "unavailable" };
    }

    const aiJson = await aiRes.json();
    const content = aiJson.choices?.[0]?.message?.content;
    if (!content) return { ok: false, reason: "unavailable" };

    try {
      const raw = JSON.parse(content);
      const riskLevel: "low" | "medium" | "high" = ["low", "medium", "high"].includes(raw.riskLevel)
        ? raw.riskLevel
        : "low";
      const riskScore =
        typeof raw.riskScore === "number"
          ? Math.max(0, Math.min(100, Math.round(raw.riskScore)))
          : 0;
      return {
        ok: true,
        notClause: Boolean(raw.notClause),
        plainEnglish: typeof raw.plainEnglish === "string" ? raw.plainEnglish : "",
        riskScore,
        riskLevel,
        watchOut: typeof raw.watchOut === "string" ? raw.watchOut : "",
      };
    } catch {
      return { ok: false, reason: "unavailable" };
    }
  });
